import { useState } from 'react';
import { dashboardT } from '../../../../lib/messages';
import { dashboardReportExplorerSearch } from '../../dashboardReporting';
import {
  DashboardWidgetType,
  type DashboardDateRange,
  type DashboardOverview,
  type DashboardWidget,
} from '../../model';
import { DashboardComparisonToggle } from '@/components/dashboard/DashboardComparisonToggle';
import { DashboardWidgetTable } from '../DashboardWidgetTable';
import { DashboardSrpmRankingTable } from './DashboardSrpmRankingTable';
import { dashboardTableSortValues } from './DashboardTableWidget.columns';
import {
  publisherColumns,
  partnerColumns,
  integrationColumns,
  networkColumns,
} from './dashboardTableColumns/dashboardTableColumns';

interface DashboardTableWidgetProps {
  widget: DashboardWidget;
  overview?: DashboardOverview | undefined;
  loading: boolean;
  error: boolean;
  range: DashboardDateRange;
}

export function DashboardTableWidget({
  widget,
  overview,
  loading,
  error,
  range,
}: DashboardTableWidgetProps) {
  const [showComparison, setShowComparison] = useState(false);
  const errorMessage = error ? dashboardT('dashboard.widget.error') : undefined;
  if (widget.type === DashboardWidgetType.PublisherPerformance) {
    return (
      <DashboardWidgetTable
        title={dashboardT('dashboard.publishers.title')}
        searchPlaceholder={dashboardT('dashboard.publishers.search')}
        items={overview?.publishers ?? []}
        columns={publisherColumns(widget.columns, showComparison)}
        sortValue={dashboardTableSortValues.publisher}
        getRowKey={(item) => item.publisherId}
        searchText={(item) => item.publisherName}
        pageSize={widget.pageSize}
        loading={loading}
        errorMessage={errorMessage}
        actions={widget.actions}
        reportingSearch={dashboardReportExplorerSearch(widget, range)}
        headerControls={
          <DashboardComparisonToggle checked={showComparison} onCheckedChange={setShowComparison} />
        }
      />
    );
  }
  if (widget.type === DashboardWidgetType.DemandIntegrations) {
    return (
      <DashboardWidgetTable
        title={dashboardT('dashboard.integrations.title')}
        searchPlaceholder={dashboardT('dashboard.integrations.search')}
        items={overview?.demandIntegrations ?? []}
        columns={integrationColumns(showComparison)}
        sortValue={dashboardTableSortValues.integration}
        getRowKey={(item) => item.demandIntegrationId ?? item.demandIntegrationName}
        searchText={(item) => item.demandIntegrationName}
        pageSize={widget.pageSize}
        loading={loading}
        errorMessage={errorMessage}
        actions={widget.actions}
        reportingSearch={dashboardReportExplorerSearch(widget, range)}
        headerControls={
          <DashboardComparisonToggle checked={showComparison} onCheckedChange={setShowComparison} />
        }
      />
    );
  }
  if (widget.type === DashboardWidgetType.DemandPartners) {
    return (
      <DashboardWidgetTable
        title={dashboardT('dashboard.partners.title')}
        searchPlaceholder={dashboardT('dashboard.partners.search')}
        items={overview?.demandPartnerAlerts ?? []}
        columns={partnerColumns(showComparison, range)}
        sortValue={(row, key) => {
          if (key === 'revenueCap' && range.dateFrom !== range.dateTo) {
            return row.revenueCapEnabled && row.revenueCap && row.revenueCap > 0
              ? row.revenueCap
              : undefined;
          }
          return dashboardTableSortValues.partner(row, key);
        }}
        getRowKey={(item) => item.demandPartnerId ?? item.demandPartnerName}
        searchText={(item) => item.demandPartnerName}
        pageSize={widget.pageSize}
        loading={loading}
        errorMessage={errorMessage}
        actions={widget.actions}
        reportingSearch={dashboardReportExplorerSearch(widget, range)}
        headerControls={
          <DashboardComparisonToggle checked={showComparison} onCheckedChange={setShowComparison} />
        }
      />
    );
  }
  if (widget.type === DashboardWidgetType.SrpmRanking) {
    return (
      <DashboardSrpmRankingTable
        widget={widget}
        topItems={overview?.top10BySRPM ?? []}
        bottomItems={overview?.bottom10BySRPM ?? []}
        loading={loading}
        errorMessage={errorMessage}
        range={range}
      />
    );
  }
  if (widget.type === DashboardWidgetType.NetworkPerformance) {
    return (
      <DashboardWidgetTable
        title={dashboardT('dashboard.networks.title')}
        searchPlaceholder={dashboardT('dashboard.networks.search')}
        items={overview?.networks ?? []}
        columns={networkColumns(widget.columns, showComparison)}
        sortValue={dashboardTableSortValues.network}
        getRowKey={(item) => item.networkId ?? item.networkName}
        searchText={(item) => item.networkName}
        pageSize={widget.pageSize}
        loading={loading}
        errorMessage={errorMessage}
        actions={widget.actions}
        reportingSearch={dashboardReportExplorerSearch(widget, range)}
        headerControls={
          <DashboardComparisonToggle checked={showComparison} onCheckedChange={setShowComparison} />
        }
      />
    );
  }
  return null;
}
